import CloseFilled16 from '@carbon/icons-react/es/close--filled/16';
import Time20 from '@carbon/icons-react/es/time/20';
import WarningFilled16 from '@carbon/icons-react/es/warning--filled/16';
import { UploadFileStatus } from 'antd/lib/upload/interface';
import classNames from 'classnames/bind';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { FileUploadStatus } from 'api/attachments';
import { CloseButton } from 'components/atoms/button';
import { DownloadButton } from 'components/atoms/button/download-button';
import { FileThumbnail } from 'components/atoms/file-thumbnail';
import { Popconfirm } from 'components/atoms/popconfirm';
import { ProgressPie } from 'components/atoms/progress-pie';
import { ProjectResource } from 'hooks/rbac/useWithAccess';
import { HideIfNotAllowed } from 'hooks/rbac/withAccess';
import { useTranslation } from 'hooks/use-translation';
import styles from './attachment-image.module.css';

type AttachmentProps = {
  originFileObj?: File | Blob;
  fileUid: string;
  fileUploadStatus?: UploadFileStatus;
  name: string;
  attachmentId?: number;
  thumbUrl?: string;
  url?: string;
  showCustomPreview: boolean;
  imageWidth?: number;
  imageHeight?: number;
  imageThumbnailClassName?: string;
  disableRemoveFile?: boolean;
  onRemove: () => void;
  onDownload?: () => void;
  onThumbnailClick?: () => void;
  large?: boolean;
  isBasicPreview?: boolean;
  isAnnotationStarted?: boolean;
  isVersionAvailable?: boolean;
  projectResource?: ProjectResource;
};

const StyledDownloadButton = styled(DownloadButton)`
  position: absolute;
  bottom: var(--spacing-02);
  right: var(--spacing-02);
  background: black;
  padding: var(--spacing-01) var(--spacing-02);
`;

const VersionIconWrapper = styled.div`
  position: absolute;
  bottom: var(--spacing-02);
  right: var(--spacing-02);
  background: black;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: var(--spacing-02);
  gap: var(--spacing-02);
  width: 28px;
  height: 28px;
  background: rgba(22, 22, 22, 0.5);
  border-radius: var(--spacing-01);
`;

const StyledVersionIcon = styled(Time20)`
  fill: #ffffff;
`;

export const AttachmentImage: FC<AttachmentProps> = ({
  originFileObj,
  fileUid,
  fileUploadStatus,
  name,
  attachmentId,
  thumbUrl,
  url,
  large,
  showCustomPreview,
  imageWidth,
  imageHeight,
  imageThumbnailClassName,
  onRemove,
  onDownload,
  onThumbnailClick,
  disableRemoveFile = false,
  isBasicPreview,
  isAnnotationStarted,
  isVersionAvailable,
  projectResource,
}) => {
  const cx = classNames.bind(styles);
  const removeAttachmentText = useTranslation('attachments.remove_file');

  const isUploading = fileUploadStatus === FileUploadStatus.Uploading;
  const itemClass = cx(imageThumbnailClassName, {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'list__item--uploading': isUploading,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    list__item__large: large,
  });
  const isFileUploadError = fileUploadStatus === FileUploadStatus.Error;

  return (
    // eslint-disable-next-line @typescript-eslint/naming-convention
    <div
      className={`${styles.list__item_wrapper}${
        large ? ` ${styles.list__item__large}` : ''
      }`}
      key={fileUid}
    >
      <FileThumbnail
        className={itemClass}
        file={originFileObj}
        fileName={name}
        id={attachmentId}
        isUploading={isUploading}
        thumbUrl={thumbUrl}
        url={url}
        showCustomPreview={showCustomPreview}
        imageWidth={imageWidth}
        imageHeight={imageHeight}
        onClick={onThumbnailClick}
        large={large}
        isBasicPreview={isBasicPreview}
        isAnnotationStarted={isAnnotationStarted}
      />
      {large && (
        <StyledDownloadButton
          onClick={(e) => {
            e.stopPropagation();
            onDownload?.();
          }}
          size="small"
          type="text"
          title="Download"
          aria-label="Download"
          className={styles.attachment__download__icon}
        />
      )}
      {isVersionAvailable && (
        <VersionIconWrapper className={styles.attachment__version__icon}>
          <StyledVersionIcon />
        </VersionIconWrapper>
      )}
      {isUploading && (
        <div className={styles.action}>
          <ProgressPie progress={10} className={styles.action__elements} />
        </div>
      )}
      <div data-test-id="remove-attachment-button-grid">
        {isFileUploadError ? (
          <>
            <div className={`${styles.action} ${styles.action__remove}`}>
              <CloseButton
                className={`action__elements ${styles.attachment__delete__icon}`}
                danger={isFileUploadError}
                aria-label={removeAttachmentText}
                icon={<CloseFilled16 />}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
              />
            </div>
            <div className={styles.action}>
              <CloseButton danger icon={<WarningFilled16 />} />
            </div>
          </>
        ) : (
          <Popconfirm
            subTitle={<FormattedMessage id="delete_attachment_confirm" />}
            onConfirm={() => {
              onRemove();
            }}
          >
            <div className={`${styles.action} ${styles.action__remove}`}>
              <HideIfNotAllowed
                rule="TASK_ATTACHMENT:WRITE"
                resourceData={projectResource}
              >
                <CloseButton
                  className={`action__elements ${styles.attachment__delete__icon}`}
                  disabled={disableRemoveFile}
                  danger={isFileUploadError}
                  aria-label={removeAttachmentText}
                  icon={<CloseFilled16 />}
                />
              </HideIfNotAllowed>
            </div>
          </Popconfirm>
        )}
      </div>
    </div>
  );
};
